import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Prospect, ProspectStatut } from '@/types/prospection'
import toast from 'react-hot-toast'

export interface UseProspectsParams {
  search?: string
  statut?: ProspectStatut | ''
  hasWebsite?: boolean | null
  tag?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export function useProspectsList(params: UseProspectsParams = {}) {
  const { search, statut, hasWebsite, tag, page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc' } = params

  return useQuery({
    queryKey: ['prospects', params],
    queryFn: async () => {
      let query = (supabase
        .from('prospects') as any)
        .select('*', { count: 'exact' })

      if (search?.trim()) {
        const s = search.trim()
        query = query.or(
          `raison_sociale.ilike.%${s}%,nom_commercial.ilike.%${s}%,ville.ilike.%${s}%,email.ilike.%${s}%,secteur.ilike.%${s}%`
        )
      }

      if (statut) {
        query = query.eq('statut', statut)
      }

      if (hasWebsite === true) {
        query = query.eq('a_un_site', true)
      } else if (hasWebsite === false) {
        query = query.eq('a_un_site', false)
      }

      if (tag) {
        query = query.contains('tags', [tag])
      }

      query = query
        .order(sortBy, { ascending: sortOrder === 'asc', nullsFirst: false })
        .range((page - 1) * limit, page * limit - 1)

      const { data, error, count } = await query

      if (error) throw error
      return { prospects: (data || []) as Prospect[], total: count || 0 }
    },
  })
}

export function useProspectTags() {
  return useQuery({
    queryKey: ['prospects', 'tags'],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('prospects') as any)
        .select('tags')
        .not('tags', 'is', null)

      if (error) throw error
      const tagSet = new Set<string>()
      for (const row of data || []) {
        if (Array.isArray(row.tags)) {
          row.tags.forEach((t: string) => tagSet.add(t))
        }
      }
      return Array.from(tagSet).sort()
    },
    staleTime: 60_000,
  })
}

export function useProspectDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['prospects', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await (supabase
        .from('prospects') as any)
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as Prospect
    },
    enabled: !!id,
  })
}

export function useUpdateProspect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<Prospect> & { id: string }) => {
      const { data, error } = await (supabase
        .from('prospects') as any)
        .update(payload)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Prospect
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospects'] })
      toast.success('Prospect mis a jour')
    },
    onError: () => {
      toast.error('Erreur lors de la mise a jour')
    },
  })
}

export function useDeleteProspect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase
        .from('prospects') as any)
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospects'] })
      toast.success('Prospect supprime')
    },
    onError: () => {
      toast.error('Erreur lors de la suppression')
    },
  })
}
